import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";

/**
 * Seed shown when creating an Objective/KR so the author answers "why" and "how",
 * not just "what". Submitted untouched it counts as empty — see isUntouchedContextTemplate.
 */
export const CONTEXT_TEMPLATE = `## Why this, why now
(current numbers, opportunity or risk driving this goal)

## How we get there
(main approach — detailed tasks live in Priorities)

## Risks & dependencies
(what could slow this down)
`;

/** An unedited template is a form artifact, not content — never store it. */
export function isUntouchedContextTemplate(value: string | undefined): boolean {
  return (value ?? "").trim() === CONTEXT_TEMPLATE.trim();
}

/** Untouched template or blank → undefined so the API never stores placeholder text. */
export function normalizeContextForSubmit(value: string | undefined): string | undefined {
  if (!value || !value.trim() || isUntouchedContextTemplate(value)) return undefined;
  return value;
}

/**
 * Update payload for context. Blanking an editor that HAD a stored strategy is an explicit clear,
 * which the API spells `null`; blank with nothing stored stays `undefined` (nothing to change).
 */
export function contextPatchForSubmit(value: string | undefined, hadStored: boolean): string | null | undefined {
  const normalized = normalizeContextForSubmit(value);
  if (normalized !== undefined) return normalized;
  return hadStored ? null : undefined;
}

interface ContextMarkdownFieldProps {
  value: string;
  onChange: (value: string) => void;
}

/** Markdown editor for the OKR `context` field: Write textarea + Preview tab. */
export function ContextMarkdownField({ value, onChange }: ContextMarkdownFieldProps) {
  return (
    <Tabs defaultValue="write">
      <TabsList className="h-8">
        <TabsTrigger value="write" className="text-xs">Write</TabsTrigger>
        <TabsTrigger value="preview" className="text-xs">Preview</TabsTrigger>
      </TabsList>
      <TabsContent value="write">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={8}
          className="font-mono text-sm"
          placeholder="Markdown — why this goal, how we get there, what could slow it down…"
        />
      </TabsContent>
      <TabsContent value="preview">
        <div className="rounded-md border p-3 min-h-[12rem]">
          {value.trim() ? (
            <MarkdownRenderer content={value} />
          ) : (
            <p className="text-sm text-muted-foreground">Nothing to preview.</p>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
